'use client';

import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type FocusEvent as ReactFocusEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { FiX, FiUser, FiClock, FiAlertCircle, FiMinus, FiPlus } from 'react-icons/fi';
import { tw } from '../lib/twind';
import type { CampaignCallInfo } from '@callixbrasil/client-sdk';
import { campaignFormCacheKey, campaignFormRequestFromCallInfo, fetchCampaignForm } from '../lib/campaignFormClient';
import type { CampaignFormField, CampaignFormResponse } from '../lib/campaignFormTypes';

/**
 * Modal arrastável com o formulário da campanha do contato (modos mobile e inline).
 *
 * Os dados vêm de UMA requisição ao backend (/api/campaign-form/{contactId}), que
 * busca os valores no Callix pela API v1 (chave do servidor) ou pela interface web
 * (sessão) - o que responder primeiro - e o layout (linhas/rótulos) cacheado por
 * campanha. Antes eram três requisições encadeadas (config -> campos + valores)
 * pela sessão web, lentas e sujeitas a falha.
 *
 * Acessibilidade: os botões "−" e "+" do cabeçalho mudam o tamanho do formulário
 * (70% a 180%, de 10 em 10). O tamanho é aplicado como `zoom` no conteúdo do modal,
 * o que escala layout e tipografia juntos (as classes do Tailwind usam rem, então
 * mudar só o font-size não bastaria). A escolha fica salva no navegador e vale para
 * as próximas chamadas.
 */

type DragPosition = {
  x: number;
  y: number;
};

type PointerData = {
  pointerId: number | null;
  offsetX: number;
  offsetY: number;
};

type ViewportSize = {
  width: number;
  height: number;
};

type DraggableClientInfoModalProps = {
  isOpen: boolean;
  campaignCallInfo: CampaignCallInfo | null;
  /** campaignContactPhoneId da chamada (currentCall.data): é o id que o Callix web usa para achar o layout. */
  campaignContactPhoneId?: number | null;
  callDuration: number;
  isCallActive: boolean;
  onClose: () => void;
  /** Onde montar o portal (padrão: fim do body). No inline, o container do widget põe o diálogo na ordem de Tab logo depois do discador. */
  portalTarget?: HTMLElement | null;
};

const VIEWPORT_MARGIN = 16;
/** Largura do formulário em 100% (px, antes do zoom). */
const BASE_WIDTH = 520;
/** Altura usada nas contas de posição enquanto o modal ainda não foi medido. */
const FALLBACK_HEIGHT = 600;

/** Tamanho do formulário (acessibilidade): porcentagem aplicada como `zoom` ao conteúdo do modal. */
export const FORM_SCALE_STORAGE_KEY = 'callix_dialer_form_scale';
export const FORM_SCALE_MIN = 70;
export const FORM_SCALE_MAX = 180;
export const FORM_SCALE_STEP = 10;
export const FORM_SCALE_DEFAULT = 100;

/** Tamanho visual mínimo dos botões do cabeçalho (WCAG 2.5.8 pede 24px; abaixo de 100% eles não encolhem junto). */
const CONTROL_MIN_PX = 28;

// twind 0.16 não entende "bg-white/20"; a forma com bg-opacity funciona. O hover escurece (em vez de
// clarear) para o texto branco não perder contraste.
const CONTROL_BUTTON_CLASS =
  'flex flex-shrink-0 items-center justify-center rounded-md transition hover:bg-black hover:bg-opacity-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white';

// Centralizar antes da primeira pintura (sem o salto de {100,100} para o centro); no servidor, useLayoutEffect avisa
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/** Normaliza uma porcentagem para o passo e os limites do controle (valores inválidos viram 100%). */
export function clampFormScale(percent: number): number {
  if (!Number.isFinite(percent)) return FORM_SCALE_DEFAULT;
  const rounded = Math.round(percent / FORM_SCALE_STEP) * FORM_SCALE_STEP;
  return clamp(rounded, FORM_SCALE_MIN, FORM_SCALE_MAX);
}

function readStoredFormScale(): number {
  if (typeof window === 'undefined') return FORM_SCALE_DEFAULT;
  try {
    const raw = window.localStorage.getItem(FORM_SCALE_STORAGE_KEY);
    if (raw === null || raw.trim() === '') return FORM_SCALE_DEFAULT;
    return clampFormScale(Number(raw));
  } catch {
    return FORM_SCALE_DEFAULT;
  }
}

function storeFormScale(percent: number): void {
  try {
    window.localStorage.setItem(FORM_SCALE_STORAGE_KEY, String(percent));
  } catch {
    // Armazenamento indisponível (modo privado, cota cheia): o tamanho vale só nesta sessão
  }
}

/**
 * `zoom` é o que escala layout e tipografia juntos. Sem suporte (Firefox anterior ao 131)
 * os controles ficam ocultos em vez de aparecerem sem efeito. Sem `CSS.supports`
 * (ambientes de teste) assume-se suporte.
 */
function detectZoomSupport(): boolean {
  if (typeof CSS === 'undefined' || typeof CSS.supports !== 'function') return true;
  try {
    if (!CSS.supports('zoom', '1.5')) return false;
  } catch {
    return true;
  }
  // Firefox 126-130 aceita zoom mas não escala SVG inline (bug 1905023): os ícones dos controles quebrariam
  const firefox = typeof navigator !== 'undefined' ? /\bFirefox\/(\d+)/.exec(navigator.userAgent) : null;
  if (firefox && Number(firefox[1]) < 131) return false;
  return true;
}

function readViewport(): ViewportSize {
  if (typeof window === 'undefined') return { width: 1024, height: 768 };
  return { width: window.innerWidth, height: window.innerHeight };
}

function formatDuration(seconds: number): string {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hrs > 0) {
    return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function gridColsFor(count: number): string {
  if (count === 2) return 'grid-cols-2';
  if (count === 3) return 'grid-cols-3';
  if (count === 4) return 'grid-cols-4';
  if (count >= 5) return 'grid-cols-5';
  return 'grid-cols-1';
}

export function DraggableClientInfoModal({
  isOpen,
  campaignCallInfo,
  campaignContactPhoneId,
  callDuration,
  isCallActive,
  onClose,
  portalTarget,
}: DraggableClientInfoModalProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const pointerRef = useRef<PointerData>({ pointerId: null, offsetX: 0, offsetY: 0 });
  const dragMovedRef = useRef(false);
  const focusWithinRef = useRef(false);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  const titleId = useId();

  const [position, setPosition] = useState<DragPosition>({ x: 100, y: 100 });
  const [isDragging, setIsDragging] = useState(false);
  const [viewport, setViewport] = useState<ViewportSize>(readViewport);

  // Tamanho do formulário (acessibilidade)
  const zoomSupported = useMemo(detectZoomSupport, []);
  const [scalePercent, setScalePercent] = useState<number>(readStoredFormScale);
  // Texto da região live: só muda nos botões − e +. No botão central o nome acessível (focado) já muda.
  const [announcement, setAnnouncement] = useState('');
  const scale = zoomSupported ? scalePercent / 100 : 1;
  const scaleRef = useRef(scale);
  scaleRef.current = scale;

  // Dados do contato
  const [form, setForm] = useState<CampaignFormResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retry, setRetry] = useState<{ key: string | null; n: number }>({ key: null, n: 0 });

  const campaignContact = campaignCallInfo?.campaignContact;
  const request = useMemo(
    () => campaignFormRequestFromCallInfo(campaignCallInfo, campaignContactPhoneId),
    [campaignCallInfo, campaignContactPhoneId]
  );
  const requestKey = request ? campaignFormCacheKey(request) : null;
  const requestRef = useRef(request);
  requestRef.current = request;
  const partialRefreshKeyRef = useRef<string | null>(null);

  /**
   * Tamanho visual do modal (já com o zoom do conteúdo). `getBoundingClientRect` inclui o
   * zoom em todos os navegadores; `offsetWidth` não inclui em alguns.
   */
  const measure = useCallback((): { width: number; height: number } => {
    const rect = containerRef.current?.getBoundingClientRect();
    const currentScale = scaleRef.current;
    return {
      width: rect && rect.width > 0 ? rect.width : BASE_WIDTH * currentScale,
      height: rect && rect.height > 0 ? rect.height : FALLBACK_HEIGHT * currentScale,
    };
  }, []);

  const clampPositionToViewport = useCallback(
    (proposed: DragPosition) => {
      if (typeof window === 'undefined') {
        return proposed;
      }

      const { width, height } = measure();
      const minX = VIEWPORT_MARGIN;
      const minY = VIEWPORT_MARGIN;
      const maxX = Math.max(minX, window.innerWidth - width - VIEWPORT_MARGIN);
      const maxY = Math.max(minY, window.innerHeight - height - VIEWPORT_MARGIN);

      return {
        x: clamp(proposed.x, minX, maxX),
        y: clamp(proposed.y, minY, maxY),
      };
    },
    [measure]
  );

  // Centralizar modal ao abrir (e reler o viewport, que pode ter mudado enquanto estava fechado)
  useIsomorphicLayoutEffect(() => {
    if (isOpen && typeof window !== 'undefined') {
      setViewport(readViewport());
      const { width, height } = measure();
      const centerX = (window.innerWidth - width) / 2;
      const centerY = (window.innerHeight - height) / 2;
      setPosition(clampPositionToViewport({ x: centerX, y: centerY }));
    }
  }, [isOpen, measure, clampPositionToViewport]);

  // Ajustar posição ao redimensionar viewport
  useEffect(() => {
    if (!isOpen || typeof window === 'undefined') {
      return;
    }

    const handleViewportChange = () => {
      setViewport(readViewport());
      setPosition((prev) => clampPositionToViewport(prev));
    };

    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('orientationchange', handleViewportChange);

    return () => {
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('orientationchange', handleViewportChange);
    };
  }, [isOpen, clampPositionToViewport]);

  const keepInsideViewport = useCallback(() => {
    setPosition((prev) => {
      const next = clampPositionToViewport(prev);
      return next.x === prev.x && next.y === prev.y ? prev : next;
    });
  }, [clampPositionToViewport]);

  // Mudou o tamanho: o modal já foi renderizado com o novo zoom, então basta mantê-lo dentro da tela
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    keepInsideViewport();
  }, [isOpen, scalePercent, keepInsideViewport]);

  // O conteúdo muda de altura depois de aberto (formulário carregado, erro, mais linhas):
  // sem isto, um modal centralizado ainda vazio cresce para fora da tela por baixo
  useEffect(() => {
    const element = containerRef.current;
    if (!isOpen || !element || typeof ResizeObserver === 'undefined') {
      return;
    }
    const observer = new ResizeObserver(() => keepInsideViewport());
    observer.observe(element);
    return () => observer.disconnect();
  }, [isOpen, keepInsideViewport]);

  // Buscar o formulário assim que a chamada de campanha é conhecida (mesmo com o modal fechado):
  // ao abrir, os dados já estão prontos. Uma requisição, com cache por contato e retry.
  useEffect(() => {
    const current = requestRef.current;
    if (!current || !requestKey) {
      setForm(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    let refreshTimer: ReturnType<typeof setTimeout> | null = null;
    setIsLoading(true);
    setError(null);

    fetchCampaignForm(current, { force: retry.key === requestKey && retry.n > 0 })
      .then((result) => {
        if (!cancelled) {
          console.log('[DraggableClientInfoModal] ✅ Formulário carregado:', result.valuesSource, result.layoutSource, result.fields.length, 'campos', result.partial ? '(parcial)' : '');
          setForm(result);
          setIsLoading(false);
          // Rótulos técnicos (valores por código, sem layout): refaz uma vez em seguida
          if (result.partial && partialRefreshKeyRef.current !== requestKey) {
            partialRefreshKeyRef.current = requestKey;
            refreshTimer = setTimeout(() => setRetry((prev) => ({ key: requestKey, n: prev.n + 1 })), 3000);
          }
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          console.error('[DraggableClientInfoModal] ❌ Erro ao buscar o formulário:', err);
          setForm(null);
          setError(err instanceof Error ? err.message : 'Erro ao buscar dados do contato');
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
      if (refreshTimer) clearTimeout(refreshTimer);
    };
  }, [requestKey, retry]);

  // Gerenciar drag
  useEffect(() => {
    if (!isDragging) {
      return;
    }

    const handlePointerMove = (event: PointerEvent) => {
      if (pointerRef.current.pointerId !== event.pointerId) {
        return;
      }

      dragMovedRef.current = true;

      const { width: modalWidth, height: modalHeight } = measure();
      const nextX = event.clientX - pointerRef.current.offsetX;
      const nextY = event.clientY - pointerRef.current.offsetY;
      const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 0;
      const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 0;
      const maxX = viewportWidth - modalWidth - VIEWPORT_MARGIN;
      const maxY = viewportHeight - modalHeight - VIEWPORT_MARGIN;
      const safeMaxX = Math.max(VIEWPORT_MARGIN, maxX);
      const safeMaxY = Math.max(VIEWPORT_MARGIN, maxY);

      setPosition({
        x: clamp(nextX, VIEWPORT_MARGIN, safeMaxX),
        y: clamp(nextY, VIEWPORT_MARGIN, safeMaxY),
      });
    };

    const handlePointerUp = (event: PointerEvent) => {
      if (pointerRef.current.pointerId !== event.pointerId) {
        return;
      }

      setIsDragging(false);
      pointerRef.current = { pointerId: null, offsetX: 0, offsetY: 0 };
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
    };
  }, [isDragging, measure]);

  const beginDrag = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 && event.pointerType !== 'touch') {
      return;
    }

    // Os botões do cabeçalho (tamanho, fechar) são clicados, não arrastados
    if (event.target instanceof Element && event.target.closest('button')) {
      return;
    }

    const element = containerRef.current;
    if (!element) {
      return;
    }

    // Sem isto o arrasto seleciona texto da página do host; a captura mantém os eventos
    // chegando mesmo com o ponteiro fora da janela
    event.preventDefault();
    const handle = event.currentTarget;
    if (typeof handle.setPointerCapture === 'function') {
      try {
        handle.setPointerCapture(event.pointerId);
      } catch {
        // ponteiro já liberado: segue sem captura
      }
    }

    const rect = element.getBoundingClientRect();
    pointerRef.current = {
      pointerId: event.pointerId,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
    };
    dragMovedRef.current = false;
    setIsDragging(true);
  }, []);

  const handleCloseClick = useCallback(() => {
    if (!isCallActive && onClose) {
      onClose();
    }
  }, [isCallActive, onClose]);

  const handleRetry = useCallback(() => {
    setRetry((prev) => ({ key: requestKey, n: prev.n + 1 }));
  }, [requestKey]);

  const applyScale = useCallback((next: number): number => {
    const value = clampFormScale(next);
    setScalePercent(value);
    storeFormScale(value);
    return value;
  }, []);

  const canDecrease = scalePercent > FORM_SCALE_MIN;
  const canIncrease = scalePercent < FORM_SCALE_MAX;

  const handleDecrease = useCallback(() => {
    if (canDecrease) setAnnouncement(`Tamanho do formulário: ${applyScale(scalePercent - FORM_SCALE_STEP)}%`);
  }, [applyScale, canDecrease, scalePercent]);

  const handleIncrease = useCallback(() => {
    if (canIncrease) setAnnouncement(`Tamanho do formulário: ${applyScale(scalePercent + FORM_SCALE_STEP)}%`);
  }, [applyScale, canIncrease, scalePercent]);

  const handleResetScale = useCallback(() => {
    applyScale(FORM_SCALE_DEFAULT);
    setAnnouncement('');
  }, [applyScale]);

  // Foco ao abrir só quando nada está focado (o agente pode estar digitando no CRM quando a chamada
  // cai); ao fechar, se o foco estava no diálogo, volta para onde estava antes
  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') {
      return;
    }
    const container = containerRef.current;
    const active = document.activeElement as HTMLElement | null;
    previouslyFocusedRef.current = active && active !== document.body ? active : null;
    if (container && (!active || active === document.body)) {
      container.focus({ preventScroll: true });
    }
    return () => {
      const previous = previouslyFocusedRef.current;
      if (focusWithinRef.current && previous && previous.isConnected) {
        previous.focus({ preventScroll: true });
      }
      focusWithinRef.current = false;
    };
  }, [isOpen]);

  const handleFocusWithin = useCallback(() => {
    focusWithinRef.current = true;
  }, []);

  const handleBlurWithin = useCallback((event: ReactFocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
      focusWithinRef.current = false;
    }
  }, []);

  // Diálogo não modal: Escape fecha (quando a chamada já terminou), como o botão de fechar
  const handleKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape' && !isCallActive) {
        event.stopPropagation();
        handleCloseClick();
      }
    },
    [handleCloseClick, isCallActive]
  );

  if (!isOpen) {
    return null;
  }

  const rows: CampaignFormField[][] = form?.rows && form.rows.length > 0 ? form.rows : [];
  const hasRows = rows.length > 0;
  const hasFields = Boolean(form && form.fields.length > 0);
  const hasData = hasRows || hasFields;

  // Limites em coordenadas do conteúdo: o zoom multiplica estes valores, então o modal
  // visual nunca passa do viewport (menos a margem) e o corpo continua rolando por dentro.
  const scaledStyle: CSSProperties = {
    zoom: scale,
    width: `${BASE_WIDTH}px`,
    maxWidth: `${Math.max(160, Math.floor((viewport.width - 2 * VIEWPORT_MARGIN) / scale))}px`,
    maxHeight: `${Math.max(120, Math.floor((viewport.height - 2 * VIEWPORT_MARGIN) / scale))}px`,
  };

  // Abaixo de 100% os botões do cabeçalho crescem na proporção inversa, para nunca ficarem
  // menores que CONTROL_MIN_PX na tela (o zoom multiplica estes valores)
  const controlPx = scale < 1 ? Math.ceil(CONTROL_MIN_PX / scale) : CONTROL_MIN_PX;
  const controlStyle: CSSProperties = { width: `${controlPx}px`, height: `${controlPx}px` };
  const percentStyle: CSSProperties = { height: `${controlPx}px`, minWidth: `${Math.ceil(48 / Math.min(scale, 1))}px` };

  const modalContent = (
    <div
      ref={containerRef}
      role="dialog"
      aria-labelledby={titleId}
      tabIndex={-1}
      onKeyDown={handleKeyDown}
      onFocus={handleFocusWithin}
      onBlur={handleBlurWithin}
      className={tw`fixed z-[9999] overflow-hidden rounded-xl border border-gray-300 bg-white text-gray-900 shadow-2xl focus:outline-none`}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        maxWidth: `calc(100vw - ${2 * VIEWPORT_MARGIN}px)`,
        maxHeight: `calc(100vh - ${2 * VIEWPORT_MARGIN}px)`,
      }}
    >
      {/* Conteúdo com zoom: layout e tipografia escalam juntos */}
      <div
        data-testid="campaign-form-scaled"
        data-scale={zoomSupported ? scalePercent : FORM_SCALE_DEFAULT}
        className={tw`flex flex-col`}
        style={scaledStyle}
      >
        {/* Header - Área arrastável */}
        <div
          onPointerDown={beginDrag}
          className={tw`flex flex-wrap cursor-move select-none items-center gap-2 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-blue-700 px-3 py-2.5 text-white`}
          style={{ touchAction: 'none' }}
        >
          {/* Em larguras pequenas (celular com zoom alto) os controles descem para uma segunda linha */}
          <div className={tw`flex min-w-[7rem] flex-1 items-center gap-2`}>
            <FiUser aria-hidden className={tw`h-5 w-5 flex-shrink-0`} />
            <h3 id={titleId} className={tw`truncate text-sm font-bold`}>
              Informações da Campanha
            </h3>
          </div>

          {zoomSupported && (
            <div role="group" aria-label="Tamanho do formulário" className={tw`ml-auto flex flex-shrink-0 items-center gap-0.5`}>
              <button
                type="button"
                onClick={handleDecrease}
                aria-disabled={!canDecrease}
                aria-label="Diminuir o tamanho do formulário"
                title="Diminuir o formulário e o texto"
                className={tw`${CONTROL_BUTTON_CLASS} ${canDecrease ? '' : 'cursor-not-allowed'}`}
                style={controlStyle}
              >
                {/* Só o ícone esmaece no limite: opacidade no botão apagaria o anel de foco */}
                <FiMinus aria-hidden className={tw`h-4 w-4 ${canDecrease ? '' : 'opacity-40'}`} />
              </button>
              <button
                type="button"
                onClick={handleResetScale}
                aria-label={`Tamanho atual ${scalePercent}%. Restaurar o tamanho padrão`}
                title="Restaurar o tamanho padrão (100%)"
                className={tw`flex-shrink-0 rounded-md px-1 text-center text-xs font-semibold tabular-nums transition hover:bg-black hover:bg-opacity-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white`}
                style={percentStyle}
              >
                {scalePercent}%
              </button>
              <button
                type="button"
                onClick={handleIncrease}
                aria-disabled={!canIncrease}
                aria-label="Aumentar o tamanho do formulário"
                title="Aumentar o formulário e o texto"
                className={tw`${CONTROL_BUTTON_CLASS} ${canIncrease ? '' : 'cursor-not-allowed'}`}
                style={controlStyle}
              >
                <FiPlus aria-hidden className={tw`h-4 w-4 ${canIncrease ? '' : 'opacity-40'}`} />
              </button>
              {/* Leitores de tela: anuncia a mudança feita por − e + */}
              <span role="status" className={tw`sr-only`}>
                {announcement}
              </span>
            </div>
          )}

          <button
            type="button"
            onClick={handleCloseClick}
            disabled={isCallActive}
            aria-label={isCallActive ? 'Aguarde o fim da chamada para fechar' : 'Fechar'}
            className={tw`${CONTROL_BUTTON_CLASS} rounded-full disabled:cursor-not-allowed disabled:opacity-40`}
            style={controlStyle}
            title={isCallActive ? 'Aguarde o fim da chamada para fechar' : 'Fechar'}
          >
            <FiX aria-hidden className={tw`h-5 w-5`} />
          </button>
        </div>

        {/* Body: região rolável focável, para rolar pelo teclado quando o zoom encurta o corpo (WCAG 2.1.1) */}
        <div
          tabIndex={0}
          role="region"
          aria-label="Dados do contato"
          className={tw`min-h-0 flex-1 space-y-3 overflow-y-auto p-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500`}
        >
          {/* Duração e informações básicas */}
          <div className={tw`grid grid-cols-2 gap-3`}>
            <div className={tw`flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2`}>
              <FiClock aria-hidden className={tw`h-4 w-4 text-gray-500`} />
              <div>
                <p className={tw`text-[10px] text-gray-500 uppercase tracking-wide font-medium`}>Duração</p>
                <p className={tw`text-sm font-bold text-gray-800`}>{formatDuration(callDuration)}</p>
              </div>
            </div>
            <div className={tw`flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2`}>
              <FiUser aria-hidden className={tw`h-4 w-4 text-gray-500`} />
              <div className={tw`min-w-0 flex-1`}>
                <p className={tw`text-[10px] text-gray-500 uppercase tracking-wide font-medium`}>Cliente</p>
                <p className={tw`truncate text-sm font-bold text-gray-800`}>
                  {campaignContact?.label || 'N/A'}
                </p>
              </div>
            </div>
          </div>

          {/* Estados de loading e erro */}
          {isLoading && (
            <div className={tw`rounded-lg border border-blue-200 bg-blue-50 p-6 text-center`} role="status">
              <div className={tw`mx-auto mb-2 inline-block h-6 w-6 animate-spin rounded-full border-2 border-blue-500 border-t-transparent`}></div>
              <p className={tw`text-xs font-medium text-blue-700`}>Carregando dados do contato...</p>
            </div>
          )}

          {error && !isLoading && (
            <div className={tw`rounded-lg border border-red-200 bg-red-50 p-3`}>
              <div className={tw`flex items-center gap-2`}>
                <FiAlertCircle aria-hidden className={tw`h-4 w-4 flex-shrink-0 text-red-600`} />
                <div className={tw`min-w-0 flex-1`}>
                  <p className={tw`text-xs font-semibold text-red-700`}>Erro ao carregar dados</p>
                  <p className={tw`text-[10px] text-red-600`}>{error}</p>
                </div>
                <button
                  type="button"
                  onClick={handleRetry}
                  className={tw`rounded-md border border-red-300 bg-white px-2 py-1 text-[11px] font-semibold text-red-700 transition hover:bg-red-100`}
                >
                  Tentar novamente
                </button>
              </div>
            </div>
          )}

          {/* Dados do contato - layout do formulário do Callix (linhas) quando disponível */}
          {!isLoading && !error && hasRows && (
            <div className={tw`space-y-2`}>
              {rows.map((row, rowIndex) => (
                <div key={rowIndex} className={tw`grid gap-2 ${gridColsFor(row.length)}`}>
                  {row.map((field, fieldIndex) => (
                    <Field
                      key={`${rowIndex}-${fieldIndex}-${field.key}`}
                      label={field.label}
                      value={field.value || '-'}
                      fullWidth={field.width === '100%'}
                    />
                  ))}
                </div>
              ))}
            </div>
          )}

          {/* Sem layout: todos os campos em duas colunas */}
          {!isLoading && !error && !hasRows && hasFields && form && (
            <div className={tw`grid grid-cols-2 gap-2`}>
              {form.fields.map((field) => (
                <Field key={field.key} label={field.label} value={field.value || '-'} />
              ))}
            </div>
          )}

          {!isLoading && !error && !hasData && (
            <div className={tw`flex flex-col items-center justify-center rounded-lg border border-gray-200 bg-gray-50 px-4 py-8 text-center`}>
              <FiUser aria-hidden className={tw`mb-2 h-8 w-8 text-gray-400`} />
              <p className={tw`text-sm text-gray-500`}>Nenhum dado disponível</p>
            </div>
          )}
        </div>

        {/* Footer */}
        {isCallActive && (
          <div className={tw`border-t border-gray-200 bg-gray-50 px-4 py-2`}>
            <p className={tw`text-center text-xs text-gray-500`}>
              Aguarde o fim da chamada para fechar
            </p>
          </div>
        )}
      </div>
    </div>
  );

  // Portal no container do widget (quando informado) ou no fim do body
  if (typeof document !== 'undefined') {
    return createPortal(modalContent, portalTarget ?? document.body);
  }

  return null;
}

// Componente auxiliar para campos
function Field({
  label,
  value,
  fullWidth,
}: {
  label: string;
  value: string;
  fullWidth?: boolean;
}) {
  // Adicionar estilo especial para campos full width
  const widthClass = fullWidth ? 'col-span-full' : '';

  return (
    <div className={tw`rounded-lg border border-gray-200 bg-white p-2 ${widthClass}`}>
      <p className={tw`mb-0.5 truncate text-[9px] font-semibold uppercase tracking-wide text-gray-500`}>
        {label}
      </p>
      {/* line-clamp não existe no twind 0.16: o corte em 2 linhas vai inline */}
      <p
        className={tw`overflow-hidden break-words text-xs font-semibold text-gray-800`}
        style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}
      >
        {value}
      </p>
    </div>
  );
}
